import React, { useState } from 'react';
import { MapPin, X, ChevronRight, Fuel, Utensils, Bath, Coffee, ShoppingBag, Camera, Wrench, Send, Pill, Phone, BadgeCheck, Navigation, Zap, AlertTriangle } from 'lucide-react';
import { SERVICES, SERVICES_VERIFIED_AT, type Service } from '../lib/servicesData';

// ─── Tipos ───────────────────────────────────────────────────────────────────

type Category = 'all' | 'fuel' | 'food' | 'restroom' | 'rest_area' | 'market' | 'scenic' | 'mechanic' | 'pharmacy' | 'ev_charging';
type PrimaryGroup = 'servicentros' | 'comercio';
type RouteFilter = 'all' | '68' | '78';

interface Props {
  onNavigateToEmergency?: () => void;
}

const CATEGORIES: { id: Category; label: string; icon: React.ReactNode; desc: string }[] = [
  { id: 'all',         label: 'Todos',         icon: null,                     desc: '' },
  { id: 'fuel',        label: 'Combustible',   icon: <Fuel size={16} />,       desc: 'Gasolineras y estaciones de servicio' },
  { id: 'ev_charging', label: 'Carga eléctrica', icon: <Zap size={16} />,      desc: 'Puntos de carga para vehículos eléctricos' },
  { id: 'food',        label: 'Comida',        icon: <Utensils size={16} />,   desc: 'Restaurantes, cafés y picadas' },
  { id: 'pharmacy',    label: 'Farmacia',      icon: <Pill size={16} />,       desc: 'Farmacias en ruta' },
  { id: 'restroom',    label: 'Baños',         icon: <Bath size={16} />,       desc: 'Servicios higiénicos en ruta' },
  { id: 'rest_area',   label: 'Descanso',      icon: <Coffee size={16} />,     desc: 'Áreas de descanso y estacionamiento' },
  { id: 'market',      label: 'Tienda',        icon: <ShoppingBag size={16} />,desc: 'Minimarkets y tiendas de conveniencia' },
  { id: 'scenic',      label: 'Panorámica',    icon: <Camera size={16} />,     desc: 'Miradores y puntos de interés' },
  { id: 'mechanic',    label: 'Mecánica',      icon: <Wrench size={16} />,     desc: 'Asistencia mecánica y grúas' },
];

// Los 3 botones que pidió el cliente: Asistencia, Servicentros y Comercio — sin opciones extra.
const PRIMARY_GROUPS: { id: PrimaryGroup; label: string; icon: React.ReactNode; desc: string; categories: Category[] }[] = [
  { id: 'servicentros', label: 'Servicentros', icon: <Fuel size={16} />,        desc: 'Combustible y carga eléctrica', categories: ['fuel', 'ev_charging'] },
  { id: 'comercio',     label: 'Comercio',     icon: <ShoppingBag size={16} />, desc: 'Comida, farmacias y tiendas',   categories: ['food', 'pharmacy', 'market'] },
];

const DIRECTION_LABEL: Record<NonNullable<Service['direction']>, string> = {
  'santiago-litoral': 'Santiago → Litoral',
  'litoral-santiago': 'Litoral → Santiago',
};

const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

// ─── Modal Sugerir Servicio ───────────────────────────────────────────────────

const SuggestModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: '', km: '', route_id: '68', category: 'food', phone: '', description: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 relative animate-fade-in">
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
          <X size={18} />
        </button>

        {sent ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Send className="text-green-600" size={28} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">¡Gracias por tu sugerencia!</h3>
            <p className="text-gray-500 text-sm">Nuestro equipo la revisará y pronto estará disponible en la app.</p>
            <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-colors">
              Cerrar
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Sugerir un servicio</h2>
            <p className="text-sm text-gray-500 mb-5">¿Conoces un lugar que debería estar aquí? Cuéntanos.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Nombre del servicio *</label>
                <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ej: Restaurant El Buen Sabor"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Ruta *</label>
                  <select value={form.route_id} onChange={e => setForm(f => ({ ...f, route_id: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                    <option value="68">Ruta 68</option>
                    <option value="78">Ruta 78</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Km aprox. *</label>
                  <input required type="number" value={form.km} onChange={e => setForm(f => ({ ...f, km: e.target.value }))}
                    placeholder="Ej: 45"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Tipo de servicio *</label>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                    <button key={c.id} type="button"
                      onClick={() => setForm(f => ({ ...f, category: c.id }))}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                        form.category === c.id ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-blue-200'
                      }`}>
                      {c.icon}{c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Teléfono (opcional)</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+56 2 2345 6789"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Descripción (opcional)</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} placeholder="Cuéntanos más sobre este servicio..."
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" />
              </div>

              <button type="submit" disabled
                className="w-full py-3 bg-gray-200 text-gray-400 rounded-xl font-bold text-sm cursor-not-allowed">
                Enviar sugerencia
              </button>
              <p className="text-[11px] text-gray-400 text-center">Próximamente — esta función estará disponible pronto</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Página principal ─────────────────────────────────────────────────────────

export const ServiciosEnRutaPage: React.FC<Props> = ({ onNavigateToEmergency }) => {
  const [routeFilter, setRouteFilter] = useState<RouteFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<PrimaryGroup>('servicentros');
  const [showModal, setShowModal] = useState(false);

  const activeGroup = PRIMARY_GROUPS.find(g => g.id === categoryFilter)!;

  const filteredServices = SERVICES.filter(s =>
    (routeFilter === 'all' || s.route_id === routeFilter) &&
    activeGroup.categories.includes(s.category)
  );

  const verifiedDate = new Date(`${SERVICES_VERIFIED_AT}T00:00:00`).toLocaleDateString('es-CL', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-black text-gray-900">Servicios en Ruta</h1>
          <p className="text-gray-500 mt-1">Gasolineras, restaurantes y más en tu camino</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar — filtros */}
          <aside className="lg:w-56 shrink-0">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 lg:sticky lg:top-20">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Ruta</p>
              <div className="flex flex-row lg:flex-col gap-2 mb-5">
                {(['all', '68', '78'] as RouteFilter[]).map(r => (
                  <button key={r}
                    onClick={() => setRouteFilter(r)}
                    className={`flex-1 lg:flex-none text-left px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                      routeFilter === r ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}>
                    {r === 'all' ? 'Todas' : `Ruta ${r}`}
                  </button>
                ))}
              </div>

              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Qué buscas</p>
              <div className="flex flex-row flex-wrap lg:flex-col gap-2 mb-6">
                <button
                  onClick={() => onNavigateToEmergency?.()}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all text-left text-red-600 bg-red-50 hover:bg-red-100">
                  <AlertTriangle size={16} />Asistencia
                </button>
                {PRIMARY_GROUPS.map(g => (
                  <button key={g.id}
                    onClick={() => setCategoryFilter(g.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all text-left ${
                      categoryFilter === g.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}>
                    {g.icon}{g.label}
                  </button>
                ))}
              </div>

              <button onClick={() => setShowModal(true)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-blue-50 border border-blue-100 text-blue-700 font-semibold text-sm hover:bg-blue-100 transition-colors">
                <span>+ Sugerir servicio</span>
                <ChevronRight size={14} />
              </button>
            </div>
          </aside>

          {/* Área principal */}
          <div className="flex-1">
            {routeFilter === '78' ? (
              // ─── Ruta 78 todavía no tiene datos del cliente ──────────────
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 px-8 py-10 text-center text-white">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4">
                    <MapPin size={32} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-black mb-2">Aún no tenemos esta información para Ruta 78</h2>
                  <p className="text-blue-100 text-sm max-w-sm mx-auto">
                    Por ahora "Servicios en Ruta" solo cubre Ruta 68. ¿Nos ayudas a compartir servicios de Ruta 78?
                  </p>
                </div>
                <div className="p-8 text-center">
                  <button onClick={() => setShowModal(true)}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors">
                    Sugerir servicio
                  </button>
                </div>
              </div>
            ) : filteredServices.length > 0 ? (
              <div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {filteredServices.map(s => {
                    const cat = CATEGORIES.find(c => c.id === s.category);
                    return (
                      <div key={s.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2 text-blue-600">
                            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                              {cat?.icon}
                            </div>
                            <p className="font-bold text-gray-900 leading-tight">{s.name}</p>
                          </div>
                          {s.is_verified && (
                            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                              <BadgeCheck size={12} /> Verificado
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mb-2">
                          <span>Ruta {s.route_id}</span>
                          {typeof s.km === 'number' && (
                            <span className="inline-flex items-center gap-1"><Navigation size={12} /> Km {s.km}</span>
                          )}
                          {s.direction && <span>{DIRECTION_LABEL[s.direction]}</span>}
                        </div>

                        {s.description && (
                          <p className="text-sm text-gray-600 mb-3">{s.description}</p>
                        )}

                        {s.phone && (
                          <a href={telHref(s.phone)}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-800">
                            <Phone size={14} /> {s.phone}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="text-xs text-gray-400 mt-4">
                  Información verificada por nuestro equipo el {verifiedDate}. Cobertura actual: solo Ruta 68.
                </p>

                <div className="mt-6 p-5 rounded-2xl bg-blue-50 border border-blue-100 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 text-sm">¿Conoces un servicio en ruta?</p>
                    <p className="text-gray-500 text-xs mt-0.5">Ayúdanos a construir el mapa. Tu sugerencia será revisada por nuestro equipo.</p>
                  </div>
                  <button onClick={() => setShowModal(true)}
                    className="shrink-0 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors">
                    Sugerir servicio
                  </button>
                </div>
              </div>
            ) : (
              // ─── Categoría sin datos todavía (ej. Baños, Descanso, Panorámica, Mecánica) ───
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 px-8 py-10 text-center text-white">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4">
                    <MapPin size={32} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-black mb-2">Próximamente</h2>
                  <p className="text-blue-100 text-sm max-w-sm mx-auto">
                    Todavía no tenemos servicios verificados en esta categoría.
                  </p>
                </div>
                <div className="p-8">
                  <div className="p-5 rounded-2xl bg-blue-50 border border-blue-100 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-sm">¿Conoces un servicio en ruta?</p>
                      <p className="text-gray-500 text-xs mt-0.5">Ayúdanos a construir el mapa. Tu sugerencia será revisada por nuestro equipo.</p>
                    </div>
                    <button onClick={() => setShowModal(true)}
                      className="shrink-0 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors">
                      Sugerir servicio
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && <SuggestModal onClose={() => setShowModal(false)} />}
    </div>
  );
};
